"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import HeroLaw from "@/components/start/HeroLaw"
import BenefitCard from "@/components/start/LawFirmRegistration/BenefitCard"
import { useGoogleAuth } from "@/hooks/Auth/useGoogleAuth"
import { registerWithSystem } from "@/services/Auth/registerService"
import BasicInfoForm from "@/components/start/ClientRegistration/BasicInfoForm"
import AdditionalInfoModal from "@/components/start/ClientRegistration/AdditionalInfoModal"
import Swal from "sweetalert2"
import Footer from "@/components/start/Footer";

function AbogadosRegistro() {
  const [formData, setFormData] = useState({
    username: "",
    name: "",
    lastname: "",
    email: "",
    password: "",
    about_me: "",
    imageUrl: "",
    phone: "",
    NSS: "",
    RFC: "",
    position: "",
    tipoUsuario: "Lawyer",
  })

  const [showModal, setShowModal] = useState(false)
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const showLoading = () => {
    Swal.fire({
      title: 'Cargando...',
      text: 'Por favor, espera mientras procesamos tu solicitud.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    });
  };

  const showSuccess = (title, text) => {
    Swal.fire({
      title,
      text,
      icon: 'success',
      confirmButtonText: 'Continuar',
      confirmButtonColor: 'var(--navy-blue)',
    });
  };

  const showError = (title, text) => {
    Swal.fire({
      title,
      text,
      icon: 'error',
      confirmButtonText: 'Intentar de nuevo',
      confirmButtonColor: 'var(--navy-blue)',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    showLoading(); // Mostrar loader mientras se espera la respuesta

    try {
      const response = await registerWithSystem(formData);
      setShowModal(false); // Cerrar el modal automáticamente

      switch (response) {
        case "Email already in use!":
          showError('Error', 'Este correo electrónico ya está en uso. Por favor, usa otro.');
          break;
        case "UserAccount created":
          showSuccess('¡Registro exitoso!', 'Tu cuenta ha sido registrada correctamente. Pronto podrás iniciar sesión');
          setFormData({
            username: "",
            name: "",
            lastname: "",
            email: "",
            password: "",
            about_me: "",
            imageUrl: "",
            phone: "",
            NSS: "",
            RFC: "",
            position: "",
            tipoUsuario: "Lawyer",
          });
          break;
        default:
          showError('Error!', response);
          break;
      }
    } catch (error) {
      showError('Error', error.message || 'Ha ocurrido un error al registrar tu cuenta');
      console.error("Error al registrar usuario:", error);
    } finally {
      setIsSubmitting(false);
      
    }
  };

  const handleConsultaClick = () => {
    const form = document.getElementById("registrationForm");
    form.scrollIntoView({ behavior: "smooth" });
  };

  const handleNextStep = (e) => {
    e.preventDefault();

    // Validación de campos obligatorios
    const requiredFields = ['username', 'name', 'lastname', 'email', 'password', 'phone'];
    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      showError('Campos incompletos', 'Por favor completa todos los campos obligatorios');
      return;
    }

    // Validación de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showError('Email inválido', 'Por favor ingresa un correo electrónico válido');
      return;
    }

    setShowModal(true);
  };

  const { register, user, error } = useGoogleAuth(formData.tipoUsuario);

  // Función para manejar el registro con Google
  const handleGoogleLogin = async () => {
 

    try {
      await register();
      if (user && !error) {
        showSuccess('¡Bienvenido!', `Has iniciado sesión como ${user.name}`);
      }
    } catch (err) {
      showError('Error', 'No se pudo iniciar sesión con Google');
     
    } 
  };

  return (
    <>
      <HeroLaw
        titulo="Encuentra el Mejor Asesoramiento Legal"
        subtitulo="Conéctate con abogados expertos para resolver tus necesidades legales"
        imagenSrc="https://images.unsplash.com/photo-1505664194779-8beaceb93744?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
        imagenAlt="Oficina de abogados moderna"
        botonTexto="Empieza hoy mismo"
        onBotonClick={handleConsultaClick}
      />

      <motion.div
        className="container mx-auto px-4 py-16"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-12">
          <motion.h2
            className="text-3xl font-bold text-gray-800 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Registro de Abogado
          </motion.h2>
          <motion.p
            className="text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Complete el formulario para crear su cuenta y comenzar a recibir asesoramiento legal personalizado
          </motion.p>
        </div>

        <motion.section className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start max-w-full mx-auto">
          <BenefitCard />

          <BasicInfoForm
            formData={formData}
            handleChange={handleChange}
            handleNextStep={handleNextStep}
            login={handleGoogleLogin}
            user={user}
            error={error}
          />
        </motion.section>
      </motion.div>

      <AnimatePresence>
        {showModal && (
          <AdditionalInfoModal
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            closeModal={() => setShowModal(false)}
            isSubmitting={isSubmitting}
          />
        )}
      </AnimatePresence>
      <Footer />
    </>
  )
}

export default AbogadosRegistro
